// ------------------------------------------------
// DOMAIN CONTEXT UTILITY
// Centralized logic for detecting main-domain vs tenant-subdomain.
// Mirrors the hostname parsing in axiosInstance.js getApiBaseUrl().
// ------------------------------------------------

const MAIN_DOMAIN = process.env.REACT_APP_MAIN_DOMAIN || 'merudesk.com';

// S3 Bucket Url 
const S3_BUCKET_URL = process.env.REACT_APP_S3_BUCKET_URL || 'https://merudesk-dev-documents.s3.ap-south-1.amazonaws.com';

/**
 * Returns the tenant slug extracted from the current hostname,
 * or `null` if the user is on the main (marketing) domain.
 */
export function getTenantSlug() {
  const host = window.location.hostname;

  // Plain localhost / loopback → main domain
  if (host === 'localhost' || host === '127.0.0.1') {
    return null;
  }

  // <slug>.localhost → tenant
  if (host.endsWith('.localhost')) {
    return host.split('.')[0] || null;
  }

  // Standard domain check based on configured MAIN_DOMAIN
  if (host === MAIN_DOMAIN || host === `www.${MAIN_DOMAIN}`) {
    return null;
  }

  if (host.endsWith(`.${MAIN_DOMAIN}`)) {
    const slug = host.slice(0, host.length - MAIN_DOMAIN.length - 1);
    if (slug === 'www') return null;
    return slug || null;
  }

  // Fallback for standard 3-part production domains if MAIN_DOMAIN is not merudesk.com but we are on merudesk.com
  const parts = host.split('.');
  if (
    parts.length >= 3 &&
    parts[parts.length - 2] === 'merudesk' &&
    parts[parts.length - 1] === 'com'
  ) {
    return parts[0] || null;
  }

  // Fallback — treat unknown hosts as main domain
  return null;
}

/** True when the browser is on the main / marketing domain. */
export function isMainDomain() {
  return getTenantSlug() === null;
}

/** True when the browser is on a tenant subdomain. */
export function isTenantDomain() {
  return getTenantSlug() !== null;
}

/**
 * Build an absolute URL pointing to the main domain.
 * Useful for hard-redirecting a tenant subdomain visitor back to
 * the marketing site.
 *
 * @param {string} [path='/'] – path to append (e.g. '/onboard')
 * @returns {string} full URL like https://merudesk.com/onboard
 */
export function getMainDomainUrl(path = '/') {
  const host = window.location.hostname;
  const protocol = window.location.protocol;

  // Local dev
  if (host.endsWith('.localhost') || host === 'localhost' || host === '127.0.0.1') {
    return `${protocol}//localhost:${window.location.port || 3000}${path}`;
  }

  // Production / Staged Main Domain
  return `${protocol}//${MAIN_DOMAIN}${path}`;
}

/**
 * Build a URL for the current frontend host.
 * This is the safest choice for links that should stay within the tenant site
 * that triggered the email or action.
 *
 * @param {string} [path='/'] - Path to append.
 * @returns {string} full URL on the current frontend origin.
 */
export function getFrontendUrl(path = '/') {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${normalizedPath}`;
}

export function getAssetUrl(path = '') {
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${S3_BUCKET_URL}/${normalizedPath}`;
}